import { forwardRef, useState, type ChangeEvent } from 'react'
import { Paperclip, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClass } from './Button'

export interface FileButtonProps {
  /** What the button says. */
  children?: string
  /** Read by screen readers and by tests: "CSV 파일". */
  inputLabel: string
  accept?: string
  name?: string
  required?: boolean
  disabled?: boolean
  icon?: LucideIcon
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  className?: string
}

/** A file input that looks like the console's buttons and speaks Korean. The
 *  browser's own one says "Choose File · No file chosen" in whatever language
 *  the browser runs in. The real input stays in the form (name, required),
 *  just out of sight; the chosen file's name shows beside the button. */
export const FileButton = forwardRef<HTMLInputElement, FileButtonProps>(function FileButton(
  { children = '파일 고르기', inputLabel, accept, name, required, disabled, icon: Icon = Paperclip, onChange, className },
  ref,
) {
  const [chosen, setChosen] = useState('')
  return (
    <span className={cn('inline-flex min-w-0 max-w-full items-center gap-2', className)}>
      <label
        className={cn(
          buttonClass('secondary', 'sm'),
          'cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/40',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <Icon size={14} aria-hidden />
        {children}
        <input
          ref={ref}
          type="file"
          aria-label={inputLabel}
          accept={accept}
          name={name}
          required={required}
          disabled={disabled}
          className="sr-only"
          onChange={(e) => {
            setChosen(e.target.files?.[0]?.name ?? '')
            onChange?.(e)
          }}
        />
      </label>
      {chosen && (
        <span className="min-w-0 truncate text-meta text-muted-foreground" title={chosen}>
          {chosen}
        </span>
      )}
    </span>
  )
})
